/**
 * Promoted ("pinned") parameters for group nodes — Nuke-style knob promotion.
 *
 * The exposure config lives on the GROUP node's data (`group.data.exposure`),
 * so it persists with the strategy and dies with the group. It is a single
 * ordered list: param items render as editable rows on the collapsed group
 * card, headings and separators let the user organize them into sections.
 */

import { uniqueName } from '@/lib/unique-name'

export interface ExposedParamItem {
  kind: 'param'
  id: string
  nodeId: string
  /**
   * Whether the exposed value is a schema parameter or a static-mode input
   * (e.g. the condition node's B input when typed, not wired). Missing ⇒ 'param'
   * for backward compatibility with exposures saved before inputs were exposable.
   */
  source?: 'param' | 'input'
  paramName: string
  alias: string
}

export interface ExposureHeadingItem {
  kind: 'heading'
  id: string
  label: string
}

export interface ExposureSeparatorItem {
  kind: 'separator'
  id: string
}

export type ExposureItem = ExposedParamItem | ExposureHeadingItem | ExposureSeparatorItem

export interface SubnetExposure {
  items: ExposureItem[]
}

export const EMPTY_EXPOSURE: SubnetExposure = { items: [] }

export const paramItemId = (
  nodeId: string,
  paramName: string,
  source: 'param' | 'input' = 'param',
): string => `${source}:${nodeId}:${paramName}`

/**
 * The display NAME part of an alias. Default aliases are "{node} · {param}";
 * a custom (renamed) alias is the whole string. Every rename UI edits and
 * shows this tail — node context comes from the surrounding chrome (block
 * label, subtitle, or context line), never repeated inside the name itself.
 */
export function aliasTail(alias: string): string {
  const i = alias.lastIndexOf(' · ')
  return i >= 0 ? alias.slice(i + 3) : alias
}

/** Safe read from arbitrary group node data. */
export function getExposure(groupData: unknown): SubnetExposure {
  const exposure = (groupData as { exposure?: unknown } | undefined)?.exposure
  if (
    exposure &&
    typeof exposure === 'object' &&
    Array.isArray((exposure as SubnetExposure).items)
  ) {
    return exposure as SubnetExposure
  }
  return EMPTY_EXPOSURE
}

export function isParamExposed(
  exposure: SubnetExposure,
  nodeId: string,
  paramName: string,
  source: 'param' | 'input' = 'param',
): boolean {
  return exposure.items.some(
    (i) =>
      i.kind === 'param' &&
      i.nodeId === nodeId &&
      i.paramName === paramName &&
      (i.source ?? 'param') === source,
  )
}

/**
 * Rewrite the internal node ids an exposure config points at, using an old→new
 * id map. Exposed params reference member nodes by id (`nodeId` + the derived
 * `id`), and `exposureCollapsed` is a list of member node ids — when a subnet is
 * copy/pasted its children get fresh ids, so the exposure must be remapped or it
 * binds to nodes that no longer exist. Ids absent from the map are kept as-is.
 * Returns the SAME data reference when there's nothing to remap.
 */
export function remapExposureNodeIds(
  data: Record<string, unknown> | undefined,
  idMap: Map<string, string>,
): Record<string, unknown> | undefined {
  if (!data) return data
  const exposure = getExposure(data)
  const rawCollapsed = (data as { exposureCollapsed?: unknown }).exposureCollapsed
  const collapsedIds = Array.isArray(rawCollapsed)
    ? rawCollapsed.filter((x): x is string => typeof x === 'string')
    : []
  if (exposure.items.length === 0 && collapsedIds.length === 0) return data

  const mapId = (id: string) => idMap.get(id) ?? id
  const items = exposure.items.map((item) => {
    if (item.kind !== 'param') return item
    const nodeId = mapId(item.nodeId)
    return { ...item, nodeId, id: paramItemId(nodeId, item.paramName, item.source ?? 'param') }
  })

  const next: Record<string, unknown> = { ...data, exposure: { ...exposure, items } }
  if (collapsedIds.length > 0) next.exposureCollapsed = collapsedIds.map(mapId)
  return next
}

/** Aliases must be unique within a group; a taken one is numbered like every other copy. */
export function uniqueAlias(exposure: SubnetExposure, desired: string): string {
  const taken = exposure.items
    .filter((i) => i.kind === 'param')
    .map((i) => (i as ExposedParamItem).alias)
  return uniqueName(desired, taken)
}

export function addExposedParam(
  exposure: SubnetExposure,
  {
    nodeId,
    paramName,
    alias,
    source = 'param',
  }: { nodeId: string; paramName: string; alias: string; source?: 'param' | 'input' },
): SubnetExposure {
  if (isParamExposed(exposure, nodeId, paramName, source)) return exposure
  return {
    ...exposure,
    items: [
      ...exposure.items,
      {
        kind: 'param',
        id: paramItemId(nodeId, paramName, source),
        nodeId,
        source,
        paramName,
        alias: uniqueAlias(exposure, alias),
      },
    ],
  }
}

export function removeExposedParam(
  exposure: SubnetExposure,
  nodeId: string,
  paramName: string,
  source: 'param' | 'input' = 'param',
): SubnetExposure {
  return {
    ...exposure,
    items: exposure.items.filter(
      (i) =>
        !(
          i.kind === 'param' &&
          i.nodeId === nodeId &&
          i.paramName === paramName &&
          (i.source ?? 'param') === source
        ),
    ),
  }
}

/** Rename an exposed param's alias, keeping it unique among the OTHER items. */
export function renameExposedParam(
  exposure: SubnetExposure,
  nodeId: string,
  paramName: string,
  alias: string,
  source: 'param' | 'input' = 'param',
): SubnetExposure {
  const matches = (i: ExposureItem) =>
    i.kind === 'param' &&
    i.nodeId === nodeId &&
    i.paramName === paramName &&
    (i.source ?? 'param') === source
  const others: SubnetExposure = { items: exposure.items.filter((i) => !matches(i)) }
  const finalAlias = uniqueAlias(others, alias)
  return {
    ...exposure,
    items: exposure.items.map((i) => (matches(i) ? { ...i, alias: finalAlias } : i)),
  }
}

export function addHeading(exposure: SubnetExposure, id: string, label: string): SubnetExposure {
  return { ...exposure, items: [...exposure.items, { kind: 'heading', id, label }] }
}

export function addSeparator(exposure: SubnetExposure, id: string): SubnetExposure {
  return { ...exposure, items: [...exposure.items, { kind: 'separator', id }] }
}

export function removeExposureItem(exposure: SubnetExposure, itemId: string): SubnetExposure {
  return { ...exposure, items: exposure.items.filter((i) => i.id !== itemId) }
}

export function renameHeading(
  exposure: SubnetExposure,
  itemId: string,
  label: string,
): SubnetExposure {
  return {
    ...exposure,
    items: exposure.items.map((i) =>
      i.kind === 'heading' && i.id === itemId ? { ...i, label } : i,
    ),
  }
}

/** Swap an item with its neighbor. Same reference when already at the edge. */
export function moveExposureItem(
  exposure: SubnetExposure,
  itemId: string,
  direction: -1 | 1,
): SubnetExposure {
  const index = exposure.items.findIndex((i) => i.id === itemId)
  const target = index + direction
  if (index === -1 || target < 0 || target >= exposure.items.length) return exposure
  const items = [...exposure.items]
  ;[items[index], items[target]] = [items[target], items[index]]
  return { ...exposure, items }
}

/** Drop param items whose node was deleted/detached. Same reference if clean. */
export function pruneExposure(
  exposure: SubnetExposure,
  existingNodeIds: Set<string>,
): SubnetExposure {
  const items = exposure.items.filter((i) => i.kind !== 'param' || existingNodeIds.has(i.nodeId))
  return items.length === exposure.items.length ? exposure : { ...exposure, items }
}
